import { Grid, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { WorkingDays } from '../../experienceSettings/workingSchedule/WorkingDays';
import { PermanentUserAvailabilityDTO, UserAvailabilityService, UserPreferencesOut } from '../../api';
import RotatingLoader from '../../common/ui/RotatingLoader';

type PermanentUserAvailabilityProps = {
  userPreferences?: UserPreferencesOut;
  isLoadingUserPreferences: boolean;
  refetchUserPrefs: () => Promise<any>;
  selectedUserId?: string;
};

export const PermanentUserAvailability = ({
  userPreferences,
  isLoadingUserPreferences,
  refetchUserPrefs,
  selectedUserId,
}: PermanentUserAvailabilityProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { t } = useTranslation();
  const [isRefetching, setIsRefetching] = useState(false);
  useEffect(() => {
    const refetchDataWithNewUser = async () => {
      if (selectedUserId === userPreferences?.propel_id) {
        return;
      }
      setIsRefetching(true);
      await refetchUserPrefs();
      setIsRefetching(false);
    };

    void refetchDataWithNewUser();
  }, [selectedUserId]);

  if (isLoadingUserPreferences || isRefetching) {
    return <RotatingLoader />;
  }
  const initialValues: PermanentUserAvailabilityDTO = {
    working_days: userPreferences!.working_days!,
    user_id: selectedUserId!,
  };

  const handleSubmit = async (
    values: PermanentUserAvailabilityDTO,
    { setSubmitting }: FormikHelpers<PermanentUserAvailabilityDTO>,
  ) => {
    try {
      await UserAvailabilityService.editPermanentUserAvailability({
        working_days: values.working_days,
        user_id: selectedUserId!,
      });
      await refetchUserPrefs();
    } catch (error) {
      console.error('Failed to update user preferences:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, isSubmitting }) => (
        <Form>
          <Grid
            container
            flexDirection={isSmallScreen ? 'column' : 'row'}
            justifyContent="space-between"
            flexWrap="nowrap"
            alignItems="flex-start">
            <WorkingDays
              readOnly={false}
              initialWorkingDays={initialValues.working_days}
              workingDays={values.working_days}
              setFieldValue={setFieldValue}
              isSubmitting={isSubmitting}
              hideFixedOption
              title={t('userAvailability.standardSchedule')}
            />
            <Grid container justifyContent="flex-end" m={1} lg={1}>
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                loading={isSubmitting}>
                {t('save')}
              </LoadingButton>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
